import React, { useState } from 'react';
import { Repeat, Check, X } from 'lucide-react';
import {
  setRecurrence,
  removeRecurrence,
  type RecurrenceRule,
} from '../packages/api-client/recurrence-api';

interface Props {
  taskId: string;
  currentRule?: RecurrenceRule | null;
  onChange?: (rule: RecurrenceRule | null) => void;
}

const FREQUENCY_LABELS: Record<RecurrenceRule['frequency'], string> = {
  daily: 'day(s)',
  weekly: 'week(s)',
  monthly: 'month(s)',
  yearly: 'year(s)',
};

export function ruleLabel(rule: RecurrenceRule): string {
  const unit = FREQUENCY_LABELS[rule.frequency] ?? rule.frequency;
  return `Repeats every ${rule.interval} ${unit}`;
}

export function RecurrenceConfig({ taskId, currentRule, onChange }: Props) {
  const [rule, setRule] = useState<RecurrenceRule>(
    currentRule ?? { frequency: 'weekly', interval: 1 },
  );
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (rule.interval < 1) return;
    setSaving(true);
    try {
      await setRecurrence(taskId, rule);
      onChange?.(rule);
    } finally { setSaving(false); }
  }

  async function handleRemove() {
    setRemoving(true);
    try {
      await removeRecurrence(taskId);
      onChange?.(null);
    } finally { setRemoving(false); }
  }

  return (
    <div className="recurrence-config">
      {currentRule && (
        <div className="recurrence-active-banner" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <Repeat size={14} aria-hidden="true" /> {ruleLabel(currentRule)}
        </div>
      )}

      <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        {/* Match the sibling sidebar fields (Assigned To / Est. Hours …):
            uppercase field labels + compact form-input sizing, not the
            full-height default form-group/form-label vocabulary. */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-2)' }}>
          <div className="task-detail-field">
            <label className="task-detail-field-label" htmlFor="recurrence-frequency">Frequency</label>
            <select
              id="recurrence-frequency"
              className="form-input"
              style={{ fontSize: 'var(--font-size-sm)', height: '2rem', padding: '0 8px' }}
              value={rule.frequency}
              onChange={e => setRule(r => ({ ...r, frequency: e.target.value as RecurrenceRule['frequency'] }))}
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </select>
          </div>
          <div className="task-detail-field">
            <label className="task-detail-field-label" htmlFor="recurrence-interval">Every</label>
            <input
              id="recurrence-interval"
              type="number"
              min={1}
              className="form-input"
              style={{ fontSize: 'var(--font-size-sm)', height: '2rem', padding: '0 8px', width: '100%', minWidth: 0 }}
              value={rule.interval}
              onChange={e => setRule(r => ({ ...r, interval: Math.max(1, parseInt(e.target.value) || 1) }))}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <button className="btn btn-primary btn-sm" type="submit" disabled={saving} style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-2)' }}>
            {saving ? '…' : currentRule ? (<><Check size={14} aria-hidden="true" /> Update</>) : (<><Repeat size={14} aria-hidden="true" /> Set Recurrence</>)}
          </button>
          {currentRule && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={handleRemove}
              disabled={removing}
              style={{ color: 'var(--color-danger)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-2)' }}
            >
              {removing ? '…' : (<><X size={14} aria-hidden="true" /> Remove</>)}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
